/**
 * CTRL_13 Configuration Panel - Data Provenance & Lineage Tracking (Detailed)
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Collapse, Button, Alert, Spin, Select, Slider, Form } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

const PROVENANCE_FIELDS = [
  { value: 'data_source', label: 'Data Source' },
  { value: 'data_provenance', label: 'Data Provenance' },
  { value: 'lineage_tracked', label: 'Lineage Tracked' },
  { value: 'data_quality_score', label: 'Data Quality Score' },
  { value: 'metadata_complete', label: 'Metadata Complete' },
  { value: 'version_control', label: 'Version Control' }
];

const DATA_SOURCES = [
  's3', 'gcs', 'azure_blob', 'redshift', 'bigquery', 'snowflake',
  'postgres', 'mysql', 'mongodb', 'api', 'kafka', 'kinesis'
];

function CTRL_13_ConfigPanel({ project, projectId, controlCode = 'CTRL_13' }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [configData, setConfigData] = useState({
    enabled: false,
    required_provenance_fields: ['data_source', 'data_provenance', 'lineage_tracked'],
    require_lineage_tracking: true,
    minimum_quality_score: 80,
    allowed_data_sources: ['s3', 'gcs', 'redshift', 'bigquery'],
    require_version_control: true,
    provenance_documentation_template: ''
  });

  useEffect(() => {
    if (projectId) {
      fetchConfig();
    }
  }, [projectId]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await getControlConfig(projectId, controlCode);
      if (res.data) {
        setConfig(res.data);
        setConfigData({
          enabled: res.data.enabled || false,
          ...res.data.config
        });
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        project_id: projectId,
        control_code: controlCode,
        enabled: configData.enabled,
        config: {
          required_provenance_fields: configData.required_provenance_fields,
          require_lineage_tracking: configData.require_lineage_tracking,
          minimum_quality_score: configData.minimum_quality_score,
          allowed_data_sources: configData.allowed_data_sources,
          require_version_control: configData.require_version_control,
          provenance_documentation_template: configData.provenance_documentation_template
        }
      };

      await saveControlConfig(projectId, controlCode, payload);
      showSuccess('Configuration saved successfully');
      fetchConfig();
    } catch (error) {
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfigData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '40px' }}><Spin size="large" /></div>;
  }

  return (
    <Card>
      <div style={{ marginBottom: '20px' }}>
        <h3>CTRL_13: Data Provenance & Lineage Configuration (Detailed)</h3>
        <p style={{ color: '#666' }}>
          Configure comprehensive data provenance and lineage tracking requirements to ensure data quality and traceability
        </p>
      </div>

      <Collapse defaultActiveKey={configData.enabled ? ['1', '2'] : ['1']}>
        <Panel header="1. Enable Data Provenance Control" key="1">
          <Switch
            checked={configData.enabled}
            onChange={(checked) => updateConfig('enabled', checked)}
            checkedChildren="Enabled"
            unCheckedChildren="Disabled"
          />
          <p style={{ marginTop: '12px', color: '#666' }}>
            When enabled, events will be checked for comprehensive data provenance and lineage information
          </p>
        </Panel>

        {configData.enabled && (
          <Panel header="2. Provenance Configuration" key="2">
            <Form layout="vertical">
              <Form.Item label="Required Provenance Fields">
                <Select
                  mode="multiple"
                  value={configData.required_provenance_fields || []}
                  onChange={(value) => updateConfig('required_provenance_fields', value)}
                  style={{ width: '100%' }}
                  placeholder="Select required provenance fields"
                >
                  {PROVENANCE_FIELDS.map(field => (
                    <Option key={field.value} value={field.value}>
                      {field.label}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Fields that must be present in event metadata for provenance tracking
                </p>
              </Form.Item>

              <Form.Item label="Lineage Tracking Requirements">
                <div style={{ marginBottom: '16px' }}>
                  <Switch
                    checked={configData.require_lineage_tracking}
                    onChange={(checked) => updateConfig('require_lineage_tracking', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require lineage tracking for all datasets</span>
                </div>

                <div>
                  <Switch
                    checked={configData.require_version_control}
                    onChange={(checked) => updateConfig('require_version_control', checked)}
                    checkedChildren="Required"
                    unCheckedChildren="Optional"
                    style={{ marginRight: '12px' }}
                  />
                  <span>Require version control for data assets</span>
                </div>
              </Form.Item>

              <Form.Item label={`Minimum Data Quality Score: ${configData.minimum_quality_score}`}>
                <Slider
                  min={0}
                  max={100}
                  value={configData.minimum_quality_score}
                  onChange={(value) => updateConfig('minimum_quality_score', value)}
                  marks={{
                    0: '0',
                    50: '50',
                    80: '80',
                    100: '100'
                  }}
                  tooltip={{ formatter: (value) => `${value}%` }}
                />
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Minimum acceptable data quality score (0-100)
                </p>
              </Form.Item>

              <Form.Item label="Allowed Data Sources">
                <Select
                  mode="tags"
                  value={configData.allowed_data_sources || []}
                  onChange={(value) => updateConfig('allowed_data_sources', value)}
                  style={{ width: '100%' }}
                  placeholder="Select or enter data sources"
                >
                  {DATA_SOURCES.map(source => (
                    <Option key={source} value={source}>
                      {source.toUpperCase()}
                    </Option>
                  ))}
                </Select>
                <p style={{ marginTop: '8px', color: '#666', fontSize: '13px' }}>
                  Approved data sources for this project (add custom sources by typing)
                </p>
              </Form.Item>
            </Form>
          </Panel>
        )}
      </Collapse>

      {configData.enabled && (
        <Alert
          message="Configuration Summary"
          description={
            <div>
              <div><strong>Required Fields:</strong> {configData.required_provenance_fields?.join(', ') || 'None'}</div>
              <div><strong>Lineage Tracking:</strong> {configData.require_lineage_tracking ? 'Required' : 'Optional'}</div>
              <div><strong>Min Quality Score:</strong> {configData.minimum_quality_score}</div>
              <div><strong>Allowed Sources:</strong> {configData.allowed_data_sources?.join(', ') || 'None'}</div>
              <div><strong>Version Control:</strong> {configData.require_version_control ? 'Required' : 'Optional'}</div>
            </div>
          }
          type="info"
          showIcon
          style={{ marginTop: '16px' }}
        />
      )}

      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSave}
        loading={saving}
        style={{ marginTop: '16px' }}
        size="large"
      >
        Save Configuration
      </Button>
    </Card>
  );
}

export default CTRL_13_ConfigPanel;
